import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import './RowContextMenu.css';

/**
 * One entry in a table row's context menu.
 *
 * Declarative on purpose: a table hands `DataTable` a list of items and it owns
 * the rendering, so every table's menu behaves — and dismisses — identically.
 * A page that renders its own menu gets to invent its own escape rules, which
 * is how you end up with a menu that survives the next click.
 */
export interface RowMenuItem {
    key: string;
    label: ReactNode;
    onSelect: () => void;
    /** Shown but unselectable — e.g. "Filter to this student" when already filtered. */
    disabled?: boolean;
    /** Draws a rule above this item, grouping what follows. */
    separatorBefore?: boolean;
    /** Destructive styling. The action still has to confirm for itself. */
    danger?: boolean;
}

export interface RowMenuState {
    x: number;
    y: number;
    items: RowMenuItem[];
}

/**
 * The menu itself: fixed at the pointer, flipped back inside the viewport, and
 * dismissed by anything that would make its position a lie — a click, Escape,
 * a scroll, a resize.
 */
export function RowContextMenu({ state, onClose }: { state: RowMenuState; onClose: () => void }) {
    const ref = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState({ left: state.x, top: state.y });
    const [active, setActive] = useState(-1);

    // Measure after paint: a menu opened near the right or bottom edge would
    // otherwise render off-screen, and the first thing the user does is scroll
    // the page to find it.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const { width, height } = el.getBoundingClientRect();
        setPos({
            left: Math.max(8, Math.min(state.x, window.innerWidth - width - 8)),
            top: Math.max(8, Math.min(state.y, window.innerHeight - height - 8)),
        });
    }, [state.x, state.y]);

    useEffect(() => {
        const selectable = state.items.filter(i => !i.disabled);
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { onClose(); return; }
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setActive(i => {
                    const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
                    return (next + selectable.length) % Math.max(1, selectable.length);
                });
            }
            if (e.key === 'Enter' && active >= 0 && selectable[active]) {
                e.preventDefault();
                selectable[active].onSelect();
                onClose();
            }
        };
        // `true` — capture, so the dismiss beats any handler on the element
        // that was clicked.
        const onDown = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) onClose();
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDown, true);
        window.addEventListener('scroll', onClose, true);
        window.addEventListener('resize', onClose);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onDown, true);
            window.removeEventListener('scroll', onClose, true);
            window.removeEventListener('resize', onClose);
        };
    }, [onClose, state.items, active]);

    let selectableIndex = -1;
    return (
        <div
            ref={ref}
            className="rcm"
            style={{ left: pos.left, top: pos.top }}
            role="menu"
            onContextMenu={e => e.preventDefault()}
        >
            {state.items.map(item => {
                if (!item.disabled) selectableIndex++;
                const isActive = !item.disabled && selectableIndex === active;
                return (
                    <button
                        key={item.key}
                        type="button"
                        role="menuitem"
                        className={[
                            'rcm-item',
                            item.disabled ? 'rcm-item--disabled' : '',
                            item.danger ? 'rcm-item--danger' : '',
                            item.separatorBefore ? 'rcm-item--sep' : '',
                            isActive ? 'rcm-item--active' : '',
                        ].filter(Boolean).join(' ')}
                        disabled={item.disabled}
                        onClick={() => { item.onSelect(); onClose(); }}
                    >
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Right-click menu for anything that is not a table row.
 *
 * `DataTable` owns the row case; this is the same menu, the same dismissal
 * rules and the same positioning for a pill, card or tile. Keeping one
 * implementation is the point — a surface that hand-rolls its own menu invents
 * its own escape rules, and ends up with one that survives the next click.
 *
 *   const { openMenu, menuNode } = useContextMenu();
 *   <button onContextMenu={e => openMenu(e, [{ key: 'del', label: 'Delete', danger: true, onSelect }])} />
 *   {menuNode}
 *
 * Passing no items leaves the browser's own menu alone, rather than replacing
 * it with an empty box.
 */
export function useContextMenu() {
    const [menu, setMenu] = useState<RowMenuState | null>(null);

    const openMenu = (e: React.MouseEvent, items: RowMenuItem[]): void => {
        if (items.length === 0) return;
        e.preventDefault();
        setMenu({ x: e.clientX, y: e.clientY, items });
    };

    const closeMenu = (): void => setMenu(null);

    const menuNode = menu ? <RowContextMenu state={menu} onClose={closeMenu} /> : null;

    return { openMenu, closeMenu, menuNode };
}
